import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  MAX_ORIENTATION_POINTS,
  sagittalFromLandmarks,
  scoreApt,
} from './hip-displacement-horizontal-strategy';
import type { OrientationScore } from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';
import type { Landmark } from '@/components/posture-analysis/types';
import { IDX } from '@/components/posture-analysis/constants';

const STRATEGY_ID = 'adaptiveThresholdVprOverrideV3';
const STRATEGY_NAME = '20260713 Adaptive Threshold VPR Override v3 (PLC and locked-knee tuning)';
const DEBUG_LOG_PREFIX = '[adaptiveThresholdVprOverrideV3]';

const MAX_AUTO_POINTS = 9; // Raised because of the locked-knee boost

const MILD_ANTERIOR_TILT_DEG = 3.0;

function resolveMildAnteriorTiltDeg(_clientSex: 'Male' | 'Female' | 'Unknown'): number {
  return MILD_ANTERIOR_TILT_DEG;
}

const HARD_C_CURVE_VETO_CM = -5.0;
const SOFT_C_CURVE_PENALTY_CM = -2.5;
const C_CURVE_PENALTY_POINTS = 2;

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  tibialAntKneeFlexGuardDeg: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
  kneeHyperextensionExtremeDegMin: number;
  pelvicTiltVetoDegMin: number;
  overridePointBoost: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  tibialAntKneeFlexGuardDeg: 7.0,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
  kneeHyperextensionExtremeDegMin: 5.0,
  pelvicTiltVetoDegMin: 15.0,
  overridePointBoost: 3,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    tibialAntKneeFlexGuardDeg:
      v.tibialAntKneeFlexGuardDeg ?? DEFAULT_VETO_THRESHOLDS.tibialAntKneeFlexGuardDeg,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
    kneeHyperextensionExtremeDegMin:
      v.kneeHyperextensionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeHyperextensionExtremeDegMin,
    pelvicTiltVetoDegMin: v.pelvicTiltVetoDegMin ?? DEFAULT_VETO_THRESHOLDS.pelvicTiltVetoDegMin,
    overridePointBoost: v.overridePointBoost ?? DEFAULT_VETO_THRESHOLDS.overridePointBoost,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const bothAtLeast = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a >= threshold &&
  b >= threshold;

const bothAtMost = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a <= threshold &&
  b <= threshold;

const rad2deg = (r: number) => (r * 180) / Math.PI;

function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

function scoreSwaybackAdaptive(
  hipFwdCm: number,
  torsoOffsetCm: number,
  kneeFlex: boolean,
  pelvicTilt: number,
  rules: any,
  kneeFlexEvidenceDeg?: number,
  kneeHipAntCm?: number,
  pelvicTiltDegMin?: number,
  mildAnteriorTiltDeg: number = MILD_ANTERIOR_TILT_DEG,
  tibialInclination?: number,
  isPlcHeadKneePattern?: boolean
): OrientationScore {
  const ev: string[] = [];
  const activeSignals: string[] = [];
  let pts = 0;
  const { hipForwardCmMin, strictHip, trunkLeanLatDegMin, strictLean, strictTilt } = rules;

  if (hipFwdCm >= hipForwardCmMin) {
    pts += 2;
    activeSignals.push('sb.hipFwd.markedAnterior');
    ev.push(`[+2] Hip markedly anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm ≥ ${hipForwardCmMin} cm)`);
  } else if (hipFwdCm >= strictHip) {
    pts += 1;
    activeSignals.push('sb.hipFwd.mildAnterior');
    ev.push(`[+1] Hip anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm ≥ ${strictHip} cm)`);
  }

  if (torsoOffsetCm <= -trunkLeanLatDegMin) {
    pts += 2;
    activeSignals.push('sb.torso.posteriorShouldersStrong');
    ev.push(`[+2] Shoulders posterior to hips vs plumb ("C" curve), Δ ≈ ${torsoOffsetCm.toFixed(1)} cm`);
  } else if (torsoOffsetCm <= -strictLean) {
    pts += 1;
    activeSignals.push('sb.torso.posteriorShouldersMild');
    ev.push(`[+1] Mild posterior shoulder shift vs hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  }

  if (isFinite(pelvicTilt) && pelvicTilt <= -strictTilt) {
    pts += 1;
    activeSignals.push('sb.pelvis.posteriorTilt');
    ev.push(`[+1] Pelvic line suggests posterior tilt (~${pelvicTilt.toFixed(1)}°)`);
  }

  // Robust pelvic tilt check: if it is inferred (fake 4°), don't trigger the guard
  // unless supported by strongly anterior tibial inclination or tibial inclination >= knee flexion.
  const isTibialStrong = typeof tibialInclination === 'number' && isFinite(tibialInclination) && tibialInclination >= 8.0;
  const isTibialGreaterThanKnee =
    typeof tibialInclination === 'number' &&
    isFinite(tibialInclination) &&
    typeof kneeFlexEvidenceDeg === 'number' &&
    isFinite(kneeFlexEvidenceDeg) &&
    tibialInclination >= kneeFlexEvidenceDeg;

  const pelvicTiltIsFullAnterior =
    typeof pelvicTiltDegMin === 'number' &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin;

  const isSwaybackShapeExcluded =
    hipFwdCm < 2.5 &&
    torsoOffsetCm >= -1.0;

  const pelvicTiltIsMildAnterior =
    !pelvicTiltIsFullAnterior &&
    (isSwaybackShapeExcluded ||
      isPlcHeadKneePattern ||
      isTibialGreaterThanKnee ||
      (isFinite(pelvicTilt) &&
        (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong))));

  const degStr =
    typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)
      ? kneeFlexEvidenceDeg.toFixed(0)
      : null;

  // Do not suppress knee flexion if it is extremely severe (>= 8° Flex from MediaPipe)
  const isKneeFlexionExtreme = typeof kneeFlexEvidenceDeg === 'number' && kneeFlexEvidenceDeg >= 8.0;

  if (kneeFlex && pelvicTiltIsFullAnterior && !isKneeFlexionExtreme) {
    activeSignals.push('sb.knee.flexion.suppressedByAnteriorPelvicTilt');
    ev.push(
      degStr
        ? `[+0] Knee flexion (~${degStr}°) suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°)`
        : `[+0] Knee flexion suppressed as Swayback signal`
    );
  } else if (kneeFlex && pelvicTiltIsMildAnterior && !isKneeFlexionExtreme) {
    pts += 1;
    activeSignals.push('sb.knee.flexion.partiallySuppressedByMildAnteriorTilt');
    ev.push(
      degStr
        ? `[+1] Knee flexion (~${degStr}°) partially suppressed: pelvic tilt is mildly anterior (~${pelvicTilt.toFixed(1)}°)`
        : `[+1] Knee flexion partially suppressed`
    );
  } else if (kneeFlex) {
    pts += 2;
    activeSignals.push('sb.knee.flexion');
    ev.push(
      degStr
        ? `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (~${degStr}°)`
        : `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}°`
    );
  }

  const kneeHipSbMin = rules.kneeHipAnteriorSwaybackCmMin;
  if (
    typeof kneeHipSbMin === 'number' &&
    typeof kneeHipAntCm === 'number' &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm > kneeHipSbMin &&
    !pelvicTiltIsFullAnterior &&
    !pelvicTiltIsMildAnterior
  ) {
    pts += 1;
    activeSignals.push('sb.kneeHip.anterior');
    ev.push(`[+1] Knees anterior to hips (${kneeHipAntCm.toFixed(1)} cm > ${kneeHipSbMin} cm)`);
  }

  return { pts: Math.min(MAX_ORIENTATION_POINTS, pts), evidence: ev, activeSignals };
}

function augmentScores(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): {
  sb: { pts: number; evidence: string[]; activeSignals: string[] };
  apt: { pts: number; evidence: string[]; activeSignals: string[] };
  dynamicMax: number;
  parsed: any;
  fromLm: any;
  rules: any;
} | null {
  const rules = (getEvaluationRules() as any).posture.thresholds;
  const kneeThresholds = {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  };
  const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
  if (!parsed) return null;

  const avgCvAngle = parsed.avgCvAngle;
  const tibialInclination = parsed.tibialInclination;
  const pelvicTiltDegMin =
    input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

  const fromLm = sagittalFromLandmarks(
    input,
    context,
    kneeThresholds.kneeFlexionDegMin,
    kneeThresholds.kneeHyperextensionDegMin
  );
  const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
  const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
  const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
  const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
  const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
  const kneeFlexEvidenceDeg =
    fromLm?.kneeFlexEvidenceDeg ??
    (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : undefined);
  const kneeHipAntCm =
    fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

  const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex);

  const isPlcHeadKneePattern =
    isFinite(avgCvAngle) &&
    avgCvAngle >= 14.0 &&
    kneeFlex &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm >= -1.0;

  const isLockedKneesHeadForward =
    isFinite(avgCvAngle) &&
    avgCvAngle >= 14.0 &&
    !kneeFlex;

  const isPlcOrLockedHeadForward = isPlcHeadKneePattern || isLockedKneesHeadForward;

  const sbBase = scoreSwaybackAdaptive(
    hipFwdCm,
    torsoOffsetCm,
    kneeFlex,
    pelvicTilt,
    rules,
    kneeFlexEvidenceDeg,
    kneeHipAntCm,
    pelvicTiltDegMin,
    mildAnteriorTiltDeg,
    tibialInclination,
    isPlcOrLockedHeadForward
  );
  const aptBase = scoreApt(
    hipFwdCm,
    torsoOffsetCm,
    kneeHyperext,
    pelvicTilt,
    rules,
    input.clientSex,
    kneeHipAntCm
  );

  let sbPts = sbBase.pts;
  let aptPts = aptBase.pts;
  const sbEvidence: string[] = [...sbBase.evidence];
  const aptEvidence: string[] = [...aptBase.evidence];
  const sbSignals: string[] = [...sbBase.activeSignals];
  const aptSignals: string[] = [...aptBase.activeSignals];

  // --- Locked Knees Point Boost for APT ---
  // Only apply boost if there is already some other indication of APT (aptPts > 0)
  // to avoid classifying a completely stacked/aligned person as APT.
  if (!kneeFlex && hipFwdCm < 2.5 && aptPts > 0) {
    aptPts += 2;
    aptSignals.push('vpr.apt.knee.lockedOrHyperextended');
    aptEvidence.push(`[+2] Locked/neutral knee state (typical anterior pelvic tilt compensation)`);
  } else if (!kneeFlex && hipFwdCm < 2.5) {
    aptEvidence.push(`[INFO] Locked/neutral knee state present but APT boost not applied because no other APT indicators are present`);
  } else if (!kneeFlex && hipFwdCm >= 2.5) {
    aptEvidence.push(`[INFO] Locked/neutral knee state present but APT boost suppressed because hips are shifted anteriorly (~${hipFwdCm.toFixed(1)} cm ≥ 2.5 cm; typical of Swayback)`);
  }

  // --- Bidirectional Forward Head Posture ---
  const fhpMin = rules.fhpSwaybackDegMin ?? 10;
  const isTibialStrong = isFinite(tibialInclination) && tibialInclination >= 8.0;
  const isTibialGreaterThanKnee =
    isFinite(tibialInclination) &&
    typeof kneeFlexEvidenceDeg === 'number' &&
    isFinite(kneeFlexEvidenceDeg) &&
    tibialInclination >= kneeFlexEvidenceDeg;

  const isSwaybackShapeExcluded =
    hipFwdCm < 2.5 &&
    torsoOffsetCm >= -1.0;

  const pelvicTiltIsMildlyAnterior =
    isSwaybackShapeExcluded ||
    isPlcOrLockedHeadForward ||
    isTibialGreaterThanKnee ||
    (isFinite(pelvicTilt) && (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong)));
  const pelvicTiltIsFullyAnterior =
    isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin;

  if (isFinite(avgCvAngle) && avgCvAngle >= fhpMin) {
    if (pelvicTiltIsMildlyAnterior) {
      aptPts += 1;
      aptSignals.push('vpr.apt.forwardHeadPostureWithAnteriorTilt');
      aptEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°) with anterior pelvis representation → lordotic APT compensation`
      );
    } else {
      sbPts += 1;
      sbSignals.push('vpr.sb.forwardHeadPosture');
      sbEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°; typical swayback presentation)`
      );
    }
  } else if (
    isFinite(avgCvAngle) &&
    avgCvAngle < fhpMin &&
    pelvicTiltIsFullyAnterior
  ) {
    aptPts += 1;
    aptSignals.push('vpr.apt.neutralHeadPosture');
    aptEvidence.push(
      `[+1] Neutral/mild head posture (CV ~${avgCvAngle.toFixed(1)}° < ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°); supports APT`
    );
  }

  // --- Tibial Inclination ---
  const tibPostMin = rules.tibialPostThresholdDeg ?? 3;
  const tibAntMin = rules.tibialAntThresholdDeg ?? 3;
  if (isFinite(tibialInclination) && tibialInclination <= -tibPostMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.posteriorTibialInclination');
    sbEvidence.push(
      `[+1] Posterior tibial inclination (~${tibialInclination.toFixed(1)}° ≤ −${tibPostMin}°; knee flexion compensation)`
    );
  } else if (isFinite(tibialInclination) && tibialInclination >= tibAntMin) {
    aptPts += 1;
    aptSignals.push('vpr.apt.anteriorTibialInclination');
    aptEvidence.push(
      `[+1] Anterior tibial inclination (~${tibialInclination.toFixed(1)}° ≥ ${tibAntMin}°; hyperextension compensation)`
    );
  }


  // --- Tibial Inclination vs Knee Flexion Rule for APT/PLC ---
  if (isFinite(tibialInclination) && typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg) && tibialInclination >= kneeFlexEvidenceDeg) {
    aptPts += 2;
    aptSignals.push('vpr.apt.tibialGreaterThanKneeFlex');
    aptEvidence.push(
      `[+2] Anterior tibial inclination (~${tibialInclination.toFixed(1)}°) ≥ knee flexion (~${kneeFlexEvidenceDeg.toFixed(0)}°) → strongly favors APT/PLC representation`
    );
  }

  // --- Head Forward with Flexed Knees (PLC/LPC pattern) Boost for APT ---
  if (isPlcHeadKneePattern) {
    aptPts += 2;
    aptSignals.push('vpr.apt.plcHeadKneePattern');
    aptEvidence.push(
      `[+2] Head forward (~${avgCvAngle.toFixed(1)}°) with flexed knees (~${kneeFlexEvidenceDeg?.toFixed(0)}°) in line/ahead of pelvis → indicates APT with PLC/LPC compensation`
    );
  }

  // --- Head Forward with Locked Knees Boost for APT ---
  if (isLockedKneesHeadForward) {
    aptPts += 2;
    aptSignals.push('vpr.apt.lockedKneesHeadForward');
    aptEvidence.push(
      `[+2] Head forward (~${avgCvAngle.toFixed(1)}°) with locked/neutral knees → indicates APT pattern rather than Swayback`
    );
  }

  // --- Practitioner visual observations ---
  const obs = input.practitionerObservations;
  let toggledCount = 0;

  if (obs?.poochBelly) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.poochBelly');
    sbEvidence.push('[+1] Practitioner observation: pooch belly (swayback)');
  }
  if (obs?.flatGlutes) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.flatGlutes');
    sbEvidence.push('[+1] Practitioner observation: flat/absent glutes (swayback)');
  }
  if (obs?.ribFlare) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.ribFlare');
    aptEvidence.push('[+1] Practitioner observation: rib flare (APT)');
  }
  if (obs?.flatCervicalCurve) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.flatCervicalCurve');
    aptEvidence.push(
      '[+1] Practitioner observation: flat/reduced cervical curve (APT)'
    );
  }

  const dynamicMax = MAX_AUTO_POINTS + toggledCount;

  return {
    sb: {
      pts: Math.min(dynamicMax, sbPts),
      evidence: sbEvidence,
      activeSignals: sbSignals,
    },
    apt: {
      pts: Math.min(dynamicMax, aptPts),
      evidence: aptEvidence,
      activeSignals: aptSignals,
    },
    dynamicMax,
    parsed,
    fromLm,
    rules,
    isPlcHeadKneePattern,
  };
}

export const adaptiveThresholdVprOverrideV3Strategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_AUTO_POINTS,

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rulesBundle = getEvaluationRules() as any;
    const rules = rulesBundle.posture.thresholds;
    const veto = readVetoThresholds(rules);

    const augmented = augmentScores(input, context);
    if (!augmented) return null;

    const { sb, apt, dynamicMax, parsed, fromLm, isPlcHeadKneePattern } = augmented;

    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
    const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
    const kneeFlexDeg =
      fromLm?.kneeFlexEvidenceDeg ??
      (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : NaN);

    let kneeHyperextDeg = NaN;
    if (fromLm && input.sagittal?.landmarks?.length) {
      const s = input.sagittal;
      const side = s.sideView;
      const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
      const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
      const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
      const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
      if (H && K && A && Math.abs(A.y - H.y) > 10) {
        const kneeRawAngle = angleAtLandmark(H, K, A);
        const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
        kneeHyperextDeg = kneeDeg;
      }
    }
    if (!isFinite(kneeHyperextDeg)) {
      kneeHyperextDeg = parsed.maxKneeHyperextDeg;
    }

    const isPinned =
      !!context.asisPsisLine ||
      (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
      (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

    let isSwaybackExcluded = false;
    let isAptExcluded = false;
    let forceStacked = false;
    let hasPlc = false;
    const overrideAndVetoEvidence: string[] = [];

    const sop = input.sopMetrics;
    if (sop) {
      const hipExtensionFullBilateral = bothAtLeast(
        sop.hipExtensionLeft,
        sop.hipExtensionRight,
        veto.fullHipExtensionDeg
      );
      const obersNeutralBilateral = bothAtMost(sop.oberLeft, sop.oberRight, veto.oberNeutralDeg);
      if (hipExtensionFullBilateral || obersNeutralBilateral) {
        isSwaybackExcluded = true;
        isAptExcluded = true;
        forceStacked = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] SoP: full hip extension / neutral Ober's bilaterally → Stacked`
        );
      }

      const irTotal = sumDefined(sop.femoralIrLeft, sop.femoralIrRight);
      const erTotal = sumDefined(sop.femoralErLeft, sop.femoralErRight);
      if (irTotal !== null && erTotal !== null) {
        if (irTotal > erTotal) {
          isSwaybackExcluded = true;
          overrideAndVetoEvidence.push(`[VETO] SoP: femoral IR > ER → excludes Swayback`);
        } else if (erTotal > irTotal) {
          hasPlc = true;
          if (apt.pts > sb.pts && apt.pts >= 2) {
            overrideAndVetoEvidence.push(
              `[INFO] SoP: femoral ER > IR (Posterior Lower Compression) detected, but APT exclusion suppressed due to dominant visual APT markers (APT ${apt.pts} vs SB ${sb.pts})`
            );
          } else {
            isAptExcluded = true;
            overrideAndVetoEvidence.push(`[VETO] SoP: femoral ER > IR → excludes APT`);
          }
        }
      }

      const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
      if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(`[VETO] SoP: SLR > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`);
      }
    }

    let extremeKneeFlexOverride = false;
    const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex);
    const isTibialStrong = isFinite(parsed.tibialInclination) && parsed.tibialInclination >= 8.0;
    const pelvicTiltIsAnteriorForKneeGuard =
      isFinite(pelvicTilt) && (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong));
    const tibInc = parsed.tibialInclination;
    const tibialAntIsStrongForKneeGuard =
      isFinite(tibInc) && tibInc >= veto.tibialAntKneeFlexGuardDeg;
    const kneeFlexOverrideSuppressed = pelvicTiltIsAnteriorForKneeGuard || tibialAntIsStrongForKneeGuard;

    if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && !kneeFlexOverrideSuppressed) {
      isAptExcluded = true;
      extremeKneeFlexOverride = true;
      overrideAndVetoEvidence.push(
        `[OVERRIDE] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}°) → excludes APT and boosts Swayback`
      );
    } else if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && kneeFlexOverrideSuppressed) {
      overrideAndVetoEvidence.push(
        `[INFO] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}°) present but APT exclusion suppressed`
      );
    }

    let extremeKneeHyperextOverride = false;
    if (kneeHyperext) {
      if (isFinite(kneeHyperextDeg) && kneeHyperextDeg >= veto.kneeHyperextensionExtremeDegMin) {
        isSwaybackExcluded = true;
        extremeKneeHyperextOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: extreme knee hyperextension (~${kneeHyperextDeg.toFixed(0)}°) → excludes Swayback`
        );
      } else {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push('[VETO] Image: knee hyperextension → excludes Swayback');
      }
    }
    // Swayback exclusion if hips are not forward and trunk is not posterior (no Swayback posture definition)
    if (hipFwdCm < 2.5 && torsoOffsetCm >= -1.0) {
      isSwaybackExcluded = true;
      overrideAndVetoEvidence.push(
        `[VETO] Image: hips not shifted forward (~${hipFwdCm.toFixed(1)} cm < 2.5 cm) and trunk neutral/almost-neutral (~${torsoOffsetCm.toFixed(1)} cm ≥ -1.0 cm) → excludes Swayback`
      );
    }

    let pelvicTiltOverride = false;
    if (isFinite(pelvicTilt)) {
      if (isPinned && pelvicTilt >= veto.pelvicTiltVetoDegMin) {
        isSwaybackExcluded = true;
        pelvicTiltOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS → excludes Swayback`
        );
      }
    }

    // --- Softened C-curve Veto & Penalty Rules ---
    // If the pelvic tilt is anterior OR if the knees are locked/hyperextended/neutral OR
    // if the tibial inclination is strongly anterior with mild knee flexion,
    // the C-curve should NOT veto or penalize APT.
    const pelvicTiltDegMin =
      input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;
    const isKneeFlexionMild = !isFinite(kneeFlexDeg) || kneeFlexDeg < 8.0;
    const isAptTiltOrLockedKnees =
      (!kneeFlex) ||
      (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) ||
      (isKneeFlexionMild && isFinite(parsed.tibialInclination) && parsed.tibialInclination >= 3.0);

    if (isFinite(torsoOffsetCm)) {
      if (torsoOffsetCm > rules.strictLean) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: shoulders forward of hips → excludes Swayback`
        );
      } else if (torsoOffsetCm < HARD_C_CURVE_VETO_CM && !isAptTiltOrLockedKnees) {
        isAptExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: severe "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < ${HARD_C_CURVE_VETO_CM} cm) → excludes APT`
        );
      } else if (torsoOffsetCm < SOFT_C_CURVE_PENALTY_CM && !isAptTiltOrLockedKnees) {
        overrideAndVetoEvidence.push(
          `[PENALTY] Image: mild "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm) → −${C_CURVE_PENALTY_POINTS} APT points`
        );
      } else if (isAptTiltOrLockedKnees && torsoOffsetCm < SOFT_C_CURVE_PENALTY_CM) {
        overrideAndVetoEvidence.push(
          `[INFO] Image: "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm) present but APT veto/penalty suppressed by locked knees or anterior tilt`
        );
      }
    }

    let finalScoreSwayback = sb.pts;
    let finalScoreApt = apt.pts;

    if (
      isFinite(torsoOffsetCm) &&
      torsoOffsetCm < SOFT_C_CURVE_PENALTY_CM &&
      torsoOffsetCm >= HARD_C_CURVE_VETO_CM &&
      !isAptTiltOrLockedKnees
    ) {
      finalScoreApt = Math.max(0, finalScoreApt - C_CURVE_PENALTY_POINTS);
    }

    if (extremeKneeFlexOverride) {
      finalScoreSwayback += veto.overridePointBoost;
    }
    if (extremeKneeHyperextOverride) {
      finalScoreApt += veto.overridePointBoost;
    }
    if (pelvicTiltOverride) {
      finalScoreApt += veto.overridePointBoost;
    }
    const tibialAntIsExtremelyStrongForBoost = isFinite(tibInc) && tibInc >= 9.0;
    if (tibialAntIsExtremelyStrongForBoost && !pelvicTiltIsAnteriorForKneeGuard) {
      finalScoreApt += veto.overridePointBoost;
      overrideAndVetoEvidence.push(
        `[BOOST] Extremely strong tibial inclination guard suppressed knee-flex APT exclusion: +${veto.overridePointBoost} APT pts`
      );
    }

    if (isSwaybackExcluded) {
      finalScoreSwayback = 0;
    }
    if (isAptExcluded) {
      finalScoreApt = 0;
    }

    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let winnerEvidence: string[] = [];
    let decisionPath = '';

    if (forceStacked || (finalScoreSwayback === 0 && finalScoreApt === 0)) {
      orientation = 'Stacked';
      matched = 0;
      winnerEvidence = forceStacked
        ? []
        : ['No surviving swayback vs anterior-orientation points after exclusions.'];
      decisionPath = forceStacked ? 'veto.forceStacked' : 'noSurvivingPoints.stacked';
    } else if (finalScoreSwayback > finalScoreApt) {
      orientation = 'Swayback';
      matched = finalScoreSwayback;
      winnerEvidence = sb.evidence;
      decisionPath = 'sbSurvives';
    } else if (finalScoreApt > finalScoreSwayback) {
      if (hasPlc || isPlcHeadKneePattern) {
        orientation = 'Anterior Pelvic Tilt with Posterior Lower Compression';
        winnerEvidence = [
          ...apt.evidence,
          '[CLASSIFICATION] Upgraded to Anterior Pelvic Tilt with Posterior Lower Compression due to combined anterior tilt and posterior lower compression indicators.',
        ];
      } else {
        orientation = 'Anterior Pelvic Tilt';
        winnerEvidence = apt.evidence;
      }
      matched = finalScoreApt;
      decisionPath = 'aptSurvives';
    } else {
      if (hipFwdCm >= rules.hipForwardCmMin && finalScoreSwayback > 0) {
        orientation = 'Swayback';
        matched = finalScoreSwayback;
        winnerEvidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        if (hasPlc || isPlcHeadKneePattern) {
          orientation = 'Anterior Pelvic Tilt with Posterior Lower Compression';
          winnerEvidence = [
            ...apt.evidence,
            'Tie-break: hip stack not dominant → anterior orientation.',
            '[CLASSIFICATION] Upgraded to Anterior Pelvic Tilt with Posterior Lower Compression due to combined anterior tilt and posterior lower compression indicators.',
          ];
        } else {
          orientation = 'Anterior Pelvic Tilt';
          winnerEvidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
        }
        matched = finalScoreApt;
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    const evidence = [...overrideAndVetoEvidence, ...winnerEvidence];

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(finalScoreSwayback, finalScoreApt) / Math.max(1, dynamicMax * 2)) *
                100
            )
          )
        : Math.min(100, Math.round((matched / dynamicMax) * 100));

    logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'adaptiveThresholdVprOverrideV3 classification', {
      rawScoreSwayback: sb.pts,
      rawScoreApt: apt.pts,
      finalScoreSwayback,
      finalScoreApt,
      isSwaybackExcluded,
      isAptExcluded,
      forceStacked,
      orientation,
      decisionPath,
      hasSopMetrics: !!input.sopMetrics,
      vetoEvidence: overrideAndVetoEvidence,
    });

    return {
      orientation,
      confidencePct,
      matchedPoints: matched,
      maxPoints: dynamicMax,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence,
    };
  },
};
